import React from 'react'
import classnames from 'classnames'
import { compose } from 'redux'
import {
  CheckboxFormField,
  makeForm,
  TextFormField,
  SelectDropdownFormField,
  MaskedFormField,
  BaseComponent
} from 'belle-js'
import { countryList } from 'utils/fieldOptions'
import Analytics from 'analytics'
import styles from '../FormStyles.module.scss'
import withFormResponseHandler from '../withFormResponseHandler'
import {
  firstNameValidation,
  middleNameValidation,
  lastNameValidation,
  birthdateValidation,
  birthdateConfirmationValidation, 
  postalCodeValidation,
  cityValidation,
  stateValidation,
  phoneNumberValidation
} from 'utils/fieldValidations'

class PersonalInformationForm extends BaseComponent {

  constructor (props) {
    super(props)
    this.bind('handleBirthdateChange')
    this.bind('handleToggleMailingAddress')
    this.bind('handleToggleHomePhone')
    this.bind('handleFormValidityChange')
  }

  componentWillMount () {
    const { formData } = this.props

    this.handleFormValidityChange()

    let hideMailingAddress = false
    if (this.addressFieldsSame(formData)) {
      hideMailingAddress = true
    }

    let homeSameAsMobile = false

    if (formData.personHomePhone === formData.personMobilePhone) {
      homeSameAsMobile = true
    }

    this.setState({
      birthdateChange: false,
      hideMailingAddress,
      homeSameAsMobile,
      personHomePhone: formData.personHomePhone
    })
  }

  handleFormValidityChange () {
    if (!this.props.onFormValidityChange) return

    const { 
      initialData: {
        firstName: initialFirst,
        lastName: initialLast,
        birthdate: initialDOB,
      },
      formData: {
        firstName: filledFirst,
        lastName: filledLast,
        personBirthdate: filledDOB
      },
      onFormValidityChange,
    } = this.props

    const prefilledForm = [initialFirst, initialLast, initialDOB].every(Boolean)
    const filledForm = [filledFirst, filledLast, filledDOB].every(Boolean)

    onFormValidityChange(prefilledForm || filledForm)
  }

  handleBirthdateChange () {
    this.setState({ birthdateChange: true })
  }

  handleToggleMailingAddress (hideMailingAddress) {
    this.setState({ hideMailingAddress })
    const action = hideMailingAddress ? 'checks' : 'unchecks'
    Analytics.track(`User ${action} Mailing Address same as Home Address`)
  }

  handleToggleHomePhone (homeSameAsMobile) {
    const action = homeSameAsMobile ? 'checks' : 'unchecks'
    Analytics.track(`User ${action} Same as Mobile Phone`)
    const { formData } = this.props

    if (homeSameAsMobile) {
      this.setState({ personHomePhone: formData.personMobilePhone })
    } else {
      this.setState({ personHomePhone: '' })
    }
    this.setState({ homeSameAsMobile })
  }

  addressFieldsSame (formData) {
    const addressFields = ['Street', 'City', 'PostalCode', 'State', 'Country']
    const isEquivalent = field => (
      formData[`billing${field}`] === formData[`personMailing${field}`]
    )
    return addressFields.every(isEquivalent)
  }

  render () {
    const {
      birthdateChange,
    } = this.state
    
    const {
      formValid,
      formData,
      hideMailingAddress,
      isMobile,
      onClick,
    } = this.props

    const primaryButtonClasses = classnames(
      'button',
      'button--primary',
      !formValid ? 'disabled' : null,
    )
    const mailingStyleConfig = {}
    mailingStyleConfig[styles.sectionHidden] = this.state.hideMailingAddress

    const mailingAddressClass = classnames(mailingStyleConfig)

    if (!this.state.hideMailingAddress && this.addressFieldsSame(formData)) {
      const addressFields = ['Street', 'City', 'PostalCode', 'State', 'Country']
      addressFields.map(field => formData[`personMailing${field}`] = '')
    }

    const handleSubmitClick = () => {
      this.handleFormValidityChange()
      onClick()
      Analytics.track('User clicks Save and Continue Personal Information')
    }

    return (
      <div className={styles.Form}>
        <div className={styles.formSection}>
          <p className={styles.heading}>Legal Name</p>
          <TextFormField
            name="firstName"
            label="Name"
            className={styles.FormFieldInSection}
            placeholder="First Name"
            validations={firstNameValidation.rules}
            validationErrors={firstNameValidation.messages}
            maxLength={35}
            value={formData.firstName || ''}
            required={true}
            testId="personalInformationForm__firstName__field" />

          <TextFormField
            name="middleName"
            label="Middle Name"
            className={styles.FormFieldInSection}
            placeholder="Middle Name"
            validations={middleNameValidation.rules}
            validationErrors={middleNameValidation.messages}
            maxLength={35}
            value={formData.middleName || ''}
            required={false}
            testId="personalInformationForm__middleName__field" />

          <TextFormField
            name="lastName"
            label="Last Name"
            className={styles.FormField}
            maxLength={35}
            placeholder="Last name"
            validations={lastNameValidation.rules}
            validationErrors={lastNameValidation.messages}
            value={formData.lastName || ''}
            testId="personalInformationForm__lastName__field"
            required={true} />
        </div>

        <div className={styles.formSection}>
          <p className={styles.heading}>Date of Birth</p>

          <div className={styles.twoColumn}>
            <MaskedFormField
              name="personBirthdate"
              label="Date of Birth"
              value={formData.personBirthdate}
              validations={birthdateValidation.rules}
              className={styles.FormField}
              validationErrors={birthdateValidation.messages}
              placeholder="MM/DD/YYYY"
              mask="date"
              testId="personalInformationForm__personBirthdate__field"
              onChange={this.handleBirthdateChange}
              required={true} />

            {(birthdateChange || !formData.personBirthdate) && <MaskedFormField
              name="personBirthdateConfirmation"
              label="Date of Birth Confirmation"
              value={formData.personBirthdateConfirmation}
              validations={birthdateChange ? birthdateConfirmationValidation.rules : {}}
              className={styles.FormField}
              validationErrors={birthdateConfirmationValidation.messages}
              placeholder="Confirm MM/DD/YYYY"
              mask="date"
              testId="personalInformationForm__personBirthdateConfirmation__field"
              required={birthdateChange} />}
          </div>
        </div>

        <div className={styles.formSection}>
          <p className={styles.heading}>Primary Address</p>

          <div className={styles.twoColumn}>
            <div>
              <TextFormField
                name="billingStreet"
                label="Billing Address"
                placeholder="Street name and number"
                validations={{ isValidPrimaryAddress: true }}
                validationErrors={{ isValidPrimaryAddress: 'Is there another address you can give us? Legally, we can’t establish your account with a PO Box.' }}
                className={styles.FormFieldInSection}
                value={formData.billingStreet || ''}
                maxLength={255}
                testId="personalInformationForm__billingStreet__field"
                required={false} />
              <TextFormField
                name="billingCity"
                label="Billing City"
                placeholder="City"
                className={styles.FormFieldInSection}
                maxLength={40}
                validations={cityValidation.rules}
                validationErrors={cityValidation.messages}
                value={formData.billingCity || ''}
                testId="personalInformationForm__billingCity__field"
                required={false} />
              {!isMobile &&
              <TextFormField
                name="billingPostalCode"
                label="Billing Postal"
                placeholder="Zipcode"
                maxLength={20}
                validations={postalCodeValidation.rules}
                validationErrors={postalCodeValidation.messages}
                value={formData.billingPostalCode || ''}
                testId="personalInformationForm__billingPostalCode__field"
                required={false} />}
              {!isMobile && !hideMailingAddress &&
              <CheckboxFormField
                name="mailingSameAsHomeAddress"
                className={styles.formToggle}
                onChange={this.handleToggleMailingAddress}
                label="Mailing address is the same as Primary Address"
                testId="personalInformationForm__mailingSameAsHomeAddress__field"
                value={this.state.hideMailingAddress} />}
            </div>
            <div>
              <TextFormField
                name="billingState"
                label="State"
                placeholder="State"
                className={styles.FormFieldInSection}
                maxLength={80}
                validations={stateValidation.rules}
                validationErrors={stateValidation.messages}
                value={formData.billingState || ''}
                testId="personalInformationForm__billingState__field"
                required={false} />
              {isMobile &&
              <TextFormField
                className={styles.FormFieldInSection}
                name="billingPostalCode"
                label="Billing Postal"
                placeholder="Zipcode"
                maxLength={20}
                validations={postalCodeValidation.rules}
                validationErrors={postalCodeValidation.messages}
                value={formData.billingPostalCode || ''}
                testId="personalInformationForm__billingPostalCode__field"
                required={false} />}
              <SelectDropdownFormField
                name="billingCountry"
                label="Country"
                className={styles.FormField}
                options={countryList}
                value={formData.billingCountry || 'United States'}
                testId="personalInformationForm__billingCountry__field"
                required={false} />
              {isMobile && !hideMailingAddress &&
              <CheckboxFormField
                name="mailingSameAsHomeAddress"
                className={styles.formToggle}
                onChange={this.handleToggleMailingAddress}
                label="Mailing Address Same As Home Address"
                testId="personalInformationForm__mailingSameAsHomeAddress__field"
                value={this.state.hideMailingAddress} />}
            </div>
          </div>
        </div>

        {!hideMailingAddress &&
        <div className={mailingAddressClass}>
          <div className={styles.formSection}>
            <p className={styles.heading}>Mailing Address</p>

            <div className={styles.twoColumn}>
              <div>
                <TextFormField
                  name="personMailingStreet"
                  label="Mailing Address"
                  className={styles.FormFieldInSection}
                  placeholder="Street name and number"
                  value={formData.personMailingStreet || ''}
                  maxLength={43}
                  testId="personalInformationForm__personMailingStreet__field"
                  required={false} />
                <TextFormField
                  name="personMailingCity"
                  label="Mailing City"
                  placeholder="City"
                  className={styles.FormFieldInSection}
                  maxLength={43}
                  validations={cityValidation.rules}
                  validationErrors={cityValidation.messages}
                  value={formData.personMailingCity || ''}
                  testId="personalInformationForm__personMailingCity__field"
                  required={false} />
                {!isMobile &&
                <TextFormField
                  name="personMailingPostalCode"
                  label="Mailing Postal"
                  placeholder="Zipcode"
                  maxLength={43}
                  validations={postalCodeValidation.rules}
                  validationErrors={postalCodeValidation.messages}
                  value={formData.personMailingPostalCode || ''}
                  testId="personalInformationForm__personMailingPostalCode__field"
                  required={false} />}
              </div>
              <div>
                <TextFormField
                  name="personMailingState"
                  label="State"
                  placeholder="State"
                  className={styles.FormFieldInSection}
                  validations={stateValidation.rules}
                  validationErrors={stateValidation.messages}
                  value={formData.personMailingState || ''}
                  maxLength={43}
                  testId="personalInformationForm__personMailingState__field"
                  required={false} />
                {isMobile &&
                <TextFormField
                  className={styles.FormFieldInSection}
                  name="personMailingPostalCode"
                  label="Mailing Postal"
                  placeholder="Zipcode"
                  maxLength={43}
                  validations={postalCodeValidation.rules}
                  validationErrors={postalCodeValidation.messages}
                  value={formData.personMailingPostalCode || ''}
                  testId="personalInformationForm__personMailingPostalCode__field"
                  required={false} />}
                <SelectDropdownFormField
                  name="personMailingCountry"
                  label="Country"
                  options={countryList}
                  value={formData.personMailingCountry || 'United States'}
                  testId="personalInformationForm__personMailingCountryDropDown__field"
                  required={false} />
              </div>
            </div>
          </div>
        </div>}

        <div className={styles.twoColumn}>
          <div className={styles.formSection}>
            <p className={styles.heading}>Mobile Phone Number</p>
            <MaskedFormField
              type="tel"
              className={styles.FormField}
              name="personMobilePhone"
              label="Mobile Phone Number"
              placeholder="555-666-7777"
              mask="phoneNumber"
              minLength={10}
              required={false}
              validations={phoneNumberValidation.rules}
              validationErrors={phoneNumberValidation.messages}
              testId="personalInformationForm__personMobilePhone__field"
              value={formData.personMobilePhone || ''} />
          </div>
          <div className={styles.formSection}>
            <p className={styles.heading}>Home Phone Number</p>
            <div className="toggle">
              <MaskedFormField
                type="tel"
                name="personHomePhone"
                label="Home Phone Number"
                className={styles.FormField}
                placeholder="555-666-7777"
                mask="phoneNumber"
                required={false}
                disabled={this.state.homeSameAsMobile}
                validations={phoneNumberValidation.rules}
                validationErrors={phoneNumberValidation.messages}
                testId="personalInformationForm__phone__field"
                value={
                  this.state.homeSameAsMobile ?
                    formData.personMobilePhone : this.state.personHomePhone
                } />
            </div>
            <CheckboxFormField
              name="homeSameAsMobile"
              className={styles.formToggle}
              onChange={this.handleToggleHomePhone}
              label="Home phone is the same as mobile phone"
              testId="personalInformationForm__homeSameAsMobile__field"
              value={this.state.homeSameAsMobile} />
          </div>
        </div>

        <div className={styles.largeFormSection}>
          <p className={styles.heading}>Email Address</p>
          <TextFormField
            name="personEmail"
            label="Email"
            className={styles.FormField}
            maxLength={80}
            placeholder="name@email.com"
            value={formData.personEmail || ''}
            validations="isEmail"
            validationErrors={{
              isEmail: 'Hmm, that’s not a valid email address.'
            }}
            testId="personalInformationForm__personEmail__field"
            required={false} />
        </div>
        {
          /* eslint-disable react/button-has-type */
          <button
            className={primaryButtonClasses}
            type={formValid ? 'submit' : 'button'}
            onClick={handleSubmitClick}
            data-test-id="personalInformationForm__saveAndContinue__cta">
              Save and Continue
          </button>
        }
      </div>
    )
  }

}
export default compose(
  makeForm,
  withFormResponseHandler,
)(PersonalInformationForm)



// WEBPACK FOOTER //
// ./src/components/Onboarding/PersonalInformation/PersonalInformationForm.jsx